import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/api/client';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections, CollapsiblePanel } from '@/components/common';
import Pagination from '@/components/Pagination';
import { DataTable, TruncatedCell, type DataTableColumn } from '@/components/table';
import {
  EmptyState,
  ErrorBanner,
  FilterSelect,
  MetricChip,
  StatusBadge,
  statusVariantForLinkDisplay,
  RecipientCell,
  SourceChips,
  TableRowActions,
  Toolbar,
  ExportButtons,
} from '@/components/ui';
import ActivityLogPanel from '@/components/activity/ActivityLogPanel';
import SearchActivityTab from '@/components/activity/SearchActivityTab';
import SearchDebugPanel from '@/components/searches/SearchDebugPanel';
import { useToast } from '@/components/Toast';
import { useMe } from '@/context/MeContext';
import { canExportSearches } from '@/utils/navCapabilities';
import type { Recipient, Search, SearchLinkItem, SearchLinksStats } from '@/types';
import { formatTelegramError } from '@/utils/telegramErrors';
import { formatFiltersSummary } from '@/utils/searchFilters';
import { runSearchErrorMessage } from '@/utils/runSearchError';

type SearchDetailTab = 'results' | 'activity' | 'debug';

function formatFiltersSummaryForSearch(search: Search | null): string {
  return formatFiltersSummary(search?.filters_json ?? null, search?.name);
}

export default function SearchLinksPage() {
  const { id } = useParams<{ id: string }>();
  const { me, loading: meLoading } = useMe();
  const canViewOpsActivity = me?.permissions.canViewGlobalActivity ?? false;
  const canExport = canExportSearches(me);
  const canRun =
    !meLoading && (me?.permissions.canRunSearch ?? me?.permissions.canCreateSearch ?? false);
  const { toast } = useToast();
  const [items, setItems] = useState<SearchLinkItem[]>([]);
  const [search, setSearch] = useState<Search | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [searchName, setSearchName] = useState('');
  const [stats, setStats] = useState<SearchLinksStats | null>(null);
  const [filters, setFilters] = useState({
    delivery_status: '',
    status: '',
    source: '',
    recipient_id: '',
    q: '',
  });
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ pages: 1, total: 0 });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [runningScrape, setRunningScrape] = useState(false);
  const [activeTab, setActiveTab] = useState<SearchDetailTab>('results');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await api.searches.links(id, {
        page,
        limit: 50,
        delivery_status: filters.delivery_status || undefined,
        status: filters.status || undefined,
        source: filters.source || undefined,
        recipient_id: filters.recipient_id || undefined,
        q: filters.q || undefined,
      });
      setItems(data.items);
      setSearch(data.search);
      setSearchName(data.search.name);
      setStats(data.stats);
      setMeta({ pages: data.pages, total: data.total });
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [id, page, filters]);

  useEffect(() => {
    api.recipients.list().then(setRecipients).catch(() => setRecipients([]));
  }, []);

  useEffect(() => {
    if (activeTab !== 'results') return;
    load();
  }, [load, activeTab]);

  const hasActiveFilters = Boolean(
    filters.delivery_status || filters.status || filters.source || filters.recipient_id || filters.q
  );

  const failedOnlyEmpty = filters.delivery_status === 'FAILED' && items.length === 0 && !loading;
  const hasRuns =
    Boolean(stats?.last_scrape_at) || Number(stats?.execution_count ?? 0) > 0;
  const noRunsYet = !hasActiveFilters && !loading && !hasRuns;
  const noLinksAfterRun = !hasActiveFilters && !loading && meta.total === 0 && hasRuns;

  const runScrape = useCallback(async () => {
    if (!id) return;
    setRunningScrape(true);
    try {
      const validation = await api.scrape.validateSearch(id);
      if (validation.warnings.length) {
        validation.warnings.forEach((w) => toast(w, 'error'));
        if (!validation.ok) return;
      }
      const result = await api.scrape.runSearch(id);
      toast(
        result.jobs_queued
          ? result.job_id
            ? `Scrape job queued (${result.total_items ?? result.jobs_queued} items)`
            : `Queued ${result.jobs_queued} scrape job(s)`
          : 'No jobs queued — check search text, sources, and subscriptions',
        result.jobs_queued ? 'success' : 'error'
      );
      result.warnings.forEach((w) => toast(w, 'error'));
      await load();
    } catch (err) {
      toast(runSearchErrorMessage(err, 'Scrape failed'), 'error');
    } finally {
      setRunningScrape(false);
    }
  }, [id, toast, load]);

  const linksEmptyState = (
    <EmptyState
      title={
        failedOnlyEmpty
          ? 'No failed deliveries'
          : hasActiveFilters
            ? 'No results for selected filters'
            : noRunsYet
              ? 'No runs yet'
              : noLinksAfterRun
                ? stats?.last_run_explanation
                  ? 'No new links from latest run'
                  : 'No matching links yet'
                : 'No links found for this search yet'
      }
      description={
        failedOnlyEmpty
          ? 'All deliveries succeeded or none have been attempted.'
          : hasActiveFilters
            ? 'Adjust filters or clear them to see more results.'
            : noRunsYet
              ? 'Run this search now or wait for the next scheduled run.'
              : noLinksAfterRun
                ? stats?.last_run_explanation ||
                  'The latest run finished but no listings matched your filters. Check Pipeline for per-source details or adjust filters.'
                : 'Links appear after the collector scrapes sources for this search.'
      }
      action={
        hasActiveFilters ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              setFilters({
                delivery_status: '',
                status: '',
                source: '',
                recipient_id: '',
                q: '',
              });
              setPage(1);
            }}
          >
            Clear filters
          </button>
        ) : noRunsYet && canRun ? (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={runningScrape}
            onClick={() => void runScrape()}
          >
            {runningScrape ? '…' : 'Run now'}
          </button>
        ) : noLinksAfterRun && id ? (
          <Link to="/search-executions" className="btn btn-sm">
            View pipeline
          </Link>
        ) : (
          <Link to="/searches" className="btn btn-sm">
            Back to searches
          </Link>
        )
      }
    />
  );

  const copyUrl = useCallback(
    (url: string) => {
      navigator.clipboard.writeText(url).then(
        () => toast('URL copied', 'success'),
        () => toast('Copy failed', 'error')
      );
    },
    [toast]
  );

  const resend = useCallback(
    async (row: SearchLinkItem) => {
      if (!row.delivery_id) return;
      setResendingId(row.delivery_id);
      try {
        const result = await api.resendDelivery(row.delivery_id);
        if (result.success) {
          toast('Resent', 'success');
        } else {
          toast(formatTelegramError(result.error, result.error_code), 'error');
        }
        await load();
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Resend failed', 'error');
      } finally {
        setResendingId(null);
      }
    },
    [toast, load]
  );

  const exportParams = useMemo(
    () => ({
      status: filters.status || undefined,
      delivery_status: filters.delivery_status || undefined,
      source: filters.source || undefined,
      recipient_id: filters.recipient_id || undefined,
      q: filters.q.trim() || undefined,
    }),
    [filters]
  );

  const linkColumns = useMemo<DataTableColumn<SearchLinkItem>[]>(
    () => [
      {
        id: 'found',
        header: 'Found',
        width: 140,
        minWidth: 110,
        maxWidth: 180,
        render: (row) => (
          <TruncatedCell
            value={(row.first_seen_at || row.found_at)?.slice(0, 19).replace('T', ' ')}
            title={row.first_seen_at || row.found_at || undefined}
          />
        ),
      },
      {
        id: 'source',
        header: 'Source',
        width: 110,
        minWidth: 90,
        maxWidth: 140,
        render: (row) =>
          row.source_name ? (
            <SourceChips sources={[row.source_name]} variant="compact" />
          ) : (
            <span className="muted">—</span>
          ),
      },
      {
        id: 'title',
        header: 'Title',
        width: 180,
        minWidth: 120,
        maxWidth: 320,
        render: (row) => <TruncatedCell value={row.title} />,
      },
      {
        id: 'recipient',
        header: 'Recipient',
        width: 130,
        minWidth: 100,
        maxWidth: 180,
        render: (row) => (
          <RecipientCell
            recipient={{
              display_name: row.recipient_display_name,
              telegram_username: row.recipient_telegram_username,
              chat_id: row.recipient_chat_id,
              telegram_resolution_method: row.recipient_resolution_method,
            }}
            linkTo={row.recipient_id ? '/recipients' : undefined}
          />
        ),
      },
      {
        id: 'delivery',
        header: 'Delivery',
        width: 100,
        minWidth: 80,
        maxWidth: 120,
        resizable: false,
        render: (row) => (
          <StatusBadge variant={statusVariantForLinkDisplay(row.display_status)}>
            {row.display_status}
          </StatusBadge>
        ),
      },
      {
        id: 'error',
        header: 'Error',
        width: 160,
        minWidth: 120,
        maxWidth: 300,
        render: (row) =>
          row.error_message ? (
            <TruncatedCell
              value={formatTelegramError(row.error_message, row.error_message.split(':')[0])}
            />
          ) : (
            '—'
          ),
      },
      {
        id: 'url',
        header: 'URL',
        width: 200,
        minWidth: 160,
        maxWidth: 400,
        render: (row) => <TruncatedCell value={row.url} href={row.url} copyable />,
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 260,
        minWidth: 260,
        maxWidth: 320,
        resizable: false,
        actions: true,
        render: (row) => (
          <TableRowActions
            url={row.url}
            onCopy={() => copyUrl(row.url)}
            onResend={
              row.delivery_id &&
              (row.delivery_status === 'FAILED' || row.delivery_status === 'SKIPPED')
                ? () => resend(row)
                : undefined
            }
            resending={resendingId === row.delivery_id}
          />
        ),
      },
    ],
    [resendingId, copyUrl, resend]
  );

  return (
    <div>
      <PageHeader
        title={searchName ? `Search results — ${searchName}` : 'Search results'}
        subtitle={
          activeTab === 'debug'
            ? 'Source-by-source pipeline health without SQL'
            : activeTab === 'activity'
              ? 'Customer-friendly timeline for this search'
            : 'What this search found and what was delivered to each recipient'
        }
      >
        <div className="page-header-tabs" role="tablist" aria-label="Search detail views">
          <button
            type="button"
            role="tab"
            className={`btn btn-sm ${activeTab === 'results' ? 'btn-primary' : 'btn-ghost'}`}
            aria-selected={activeTab === 'results'}
            onClick={() => setActiveTab('results')}
          >
            Results
          </button>
          <button
            type="button"
            role="tab"
            className={`btn btn-sm ${activeTab === 'activity' ? 'btn-primary' : 'btn-ghost'}`}
            aria-selected={activeTab === 'activity'}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
          {canViewOpsActivity ? (
          <button
            type="button"
            role="tab"
            className={`btn btn-sm ${activeTab === 'debug' ? 'btn-primary' : 'btn-ghost'}`}
            aria-selected={activeTab === 'debug'}
            onClick={() => setActiveTab('debug')}
          >
            Debug &amp; Health
          </button>
          ) : null}
        </div>
        {canRun && id ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={runningScrape}
            onClick={() => void runScrape()}
          >
            {runningScrape ? 'Running…' : 'Run now'}
          </button>
        ) : null}
        <Link to="/searches" className="btn btn-ghost">
          Back to searches
        </Link>
      </PageHeader>

      {error && activeTab === 'results' && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {activeTab === 'debug' && id ? <SearchDebugPanel searchId={id} /> : null}
      {activeTab === 'activity' && id ? <SearchActivityTab searchId={id} /> : null}

      {activeTab === 'results' && search && (
        <CollapsiblePanel id="summary" storageScope={`search-links-${id}`} title="Search summary" defaultExpanded>
        <dl className="search-summary-bar">
          <div className="search-summary-item">
            <dt>Filters</dt>
            <dd>{formatFiltersSummaryForSearch(search)}</dd>
          </div>
          <div className="search-summary-item">
            <dt>Sources</dt>
            <dd>
              <SourceChips sources={search.source_names ?? []} variant="compact" />
            </dd>
          </div>
          <div className="search-summary-item">
            <dt>Last scrape</dt>
            <dd>
              {stats?.last_scrape_at
                ? new Date(stats.last_scrape_at).toLocaleString()
                : '—'}
            </dd>
          </div>
        </dl>
        </CollapsiblePanel>
      )}

      {activeTab === 'results' ? (
      <AdminPageSections>
        <CollapsiblePanel id="stats" storageScope={`search-links-${id}`} title="Results summary" defaultExpanded>
      <div className="metric-chips">
        <MetricChip label="Total" value={stats?.total_found ?? 0} />
        <MetricChip label="New" value={stats?.new_count ?? 0} variant="info" />
        <MetricChip label="Sent" value={stats?.sent_count ?? 0} variant="success" />
        <MetricChip label="Failed" value={stats?.failed_count ?? 0} variant="danger" />
        <MetricChip label="Skipped" value={stats?.skipped_count ?? 0} variant="warning" />
        <MetricChip label="Discovered only" value={stats?.discovered_only_count ?? 0} />
      </div>
        </CollapsiblePanel>

      <CollapsiblePanel id="found-links" storageScope={`search-links-${id}`} title="Found links" defaultExpanded>
        <Toolbar>
          <FilterSelect label="Delivery" htmlFor="sl-delivery">
            <select
              id="sl-delivery"
              value={filters.delivery_status}
              onChange={(e) => {
                setFilters((f) => ({ ...f, delivery_status: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="SENT">Sent</option>
              <option value="FAILED">Failed</option>
              <option value="SKIPPED">Skipped</option>
            </select>
          </FilterSelect>
          <FilterSelect label="Link status" htmlFor="sl-status">
            <select
              id="sl-status"
              value={filters.status}
              onChange={(e) => {
                setFilters((f) => ({ ...f, status: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="new">New</option>
              <option value="seen">Seen</option>
              <option value="discovered">Discovered only</option>
            </select>
          </FilterSelect>
          <FilterSelect label="Source" htmlFor="sl-source">
            <input
              id="sl-source"
              value={filters.source}
              onChange={(e) => {
                setFilters((f) => ({ ...f, source: e.target.value }));
                setPage(1);
              }}
              placeholder="e.g. mobile_de"
            />
          </FilterSelect>
          <FilterSelect label="Recipient" htmlFor="sl-recipient">
            <select
              id="sl-recipient"
              value={filters.recipient_id}
              onChange={(e) => {
                setFilters((f) => ({ ...f, recipient_id: e.target.value }));
                setPage(1);
              }}
            >
              <option value="">All</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.display_name || r.chat_id}
                </option>
              ))}
            </select>
          </FilterSelect>
          <FilterSelect label="Search text" htmlFor="sl-q">
            <input
              id="sl-q"
              value={filters.q}
              onChange={(e) => {
                setFilters((f) => ({ ...f, q: e.target.value }));
                setPage(1);
              }}
              placeholder="Title or URL"
            />
          </FilterSelect>
        </Toolbar>

        {loading && items.length === 0 ? (
          <div className="loading-inline" role="status">
            <span className="spinner" aria-hidden />
            Loading results…
          </div>
        ) : (
          <DataTable
            columns={linkColumns}
            rows={items}
            getRowKey={(row) => `${row.subscription_id}-${row.link_id}`}
            getRowClassName={(row) => (row.delivery_status === 'FAILED' ? 'row-failed' : undefined)}
            emptyState={linksEmptyState}
            columnStorageKey="search-links"
            toolbar={{
              onRefresh: () => load(),
              rightSlot: id && canExport ? (
                <ExportButtons path={`/exports/searches/${id}/links`} params={exportParams} />
              ) : undefined,
            }}
          />
        )}
        <Pagination page={page} pages={meta.pages} total={meta.total} onPage={setPage} />
      </CollapsiblePanel>

      {id ? (
        canViewOpsActivity ? (
        <CollapsiblePanel
          id="search-activity-ops"
          storageScope="search-links"
          title="Operations log"
          subtitle="Internal deliveries, fetch runs, and pipeline events"
          defaultExpanded={false}
        >
          <ActivityLogPanel searchId={id} limit={10} showClear exportPath="/exports/activity/logs" exportParams={{ search_id: id }} />
        </CollapsiblePanel>
        ) : null
      ) : null}
      </AdminPageSections>
      ) : null}
    </div>
  );
}
